import * as React from 'react';

import { Autocomplete, breakpoints, useMediaQuery } from '@coursera/cds-core';

import useCurrentRoleEditor from 'bundles/personalization-settings/hooks/useCurrentRoleEditor';
import type {
  AvailableOccupation,
  CurrentRoleValue,
} from 'bundles/personalization-settings/hooks/useCurrentRoleEditor';
import { useDebouncedPersonalizationSearchTracker } from 'bundles/personalization-settings/hooks/usePersonalizationEventTracking';

import _t from 'i18n!nls/personalization-settings';

export type CurrentRoleEditorProps = {
  /** The learner's current role (controlled): a catalog occupation, free text, or unset. */
  value: CurrentRoleValue;
  /** Called with the next value whenever a selection or applied edit replaces it. */
  onChange: (value: CurrentRoleValue) => void;
  /** Catalog occupations the learner can pick from, supplied by the parent container. */
  availableOccupations?: readonly AvailableOccupation[];
  /** ID of an external visible label element — used for aria-labelledby when a label is rendered outside the editor. */
  labelId?: string;
};

const displayName = (value: CurrentRoleValue) => value?.name ?? '';

/**
 * The "Current role" editor for the Personalization settings page: a single
 * CDS Autocomplete backed by the occupation catalog. Unlike the multi-select
 * "Desired skills" editor it mirrors visually, there is no `ChipGroup` --
 * selecting an option or applying typed text REPLACES the single value
 * rather than adding to a list.
 *
 * Controlled via `value`/`onChange`. It does not read or persist the Learner
 * Object itself -- the parent container (`CurrentRoleSettingItem`) resolves
 * `value` synchronously before this ever mounts (it renders a skeleton until
 * the saved value is seeded).
 */
const CurrentRoleEditor = ({ value, onChange, availableOccupations = [], labelId }: CurrentRoleEditorProps) => {
  const [input, setInput] = React.useState(displayName(value));
  const trackSearchInput = useDebouncedPersonalizationSearchTracker('current_role_search');
  const inputRef = React.useRef<HTMLInputElement>(null);

  const isXs = useMediaQuery(breakpoints.down('sm'));
  const skipBlurApplyRef = React.useRef(false);
  const restoreFocusRef = React.useRef<(() => void) | null>(null);

  // If the editor unmounts while a restore is still pending (learner never
  // touched the page again after the drawer closed), put `.focus` back so we
  // don't leak a neutered method onto a DOM node some other code might reuse.
  React.useEffect(() => () => restoreFocusRef.current?.(), []);

  // `value` can change from outside this editor: a failed save rolls it back to
  // the last known-good value (`useSettingSave`), and the input must follow.
  React.useEffect(() => {
    setInput(displayName(value));
  }, [value]);

  const { options, trimmedInput, selectFromOption, selectFromInput, clear } = useCurrentRoleEditor({
    value,
    onChange,
    availableOccupations,
    input,
  });

  const handleSelectionChange = (key: React.Key | null) => {
    if (key == null) return;
    const selected = options.find((option) => option.key === key);
    selectFromOption(String(key));
    if (selected) setInput(selected.label);
    // Blur so the next click re-triggers focus and reopens the dropdown --
    // popoverTrigger="focus" only opens on a focus event, and clicking a field
    // that is already focused produces none. Mirrors ChipAndAutocompleteInput.
    // The flag keeps `handleBlur` from re-applying the now-stale typed text
    // ('data' when the learner arrow-keyed to 'Data Scientist') over the
    // selection: this blur runs before the `setInput` above has committed.
    skipBlurApplyRef.current = true;
    inputRef.current?.blur();
  };

  const applyTypedInput = () => {
    if (trimmedInput === displayName(value)) return; // nothing changed
    if (!trimmedInput) {
      clear();
    } else {
      selectFromInput();
    }
  };

  // Enter applies whatever is typed -- but only when the learner hasn't
  // arrow-keyed to a specific option, mirroring SkillsEditor's guard so we
  // don't also apply the raw text when Autocomplete's own onSelectionChange
  // is about to fire for the highlighted option.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    if (event.currentTarget.getAttribute('aria-activedescendant')) return;
    event.preventDefault();
    applyTypedInput();
  };

  const handleBlur = () => {
    if (skipBlurApplyRef.current) {
      skipBlurApplyRef.current = false;
      return;
    }
    applyTypedInput();
  };

  /**
   * Below `sm` CDS renders the popover in a Drawer whose `returnFocusRef` calls
   * `inputRef.current.focus()` on close. With popoverTrigger="focus" that
   * synthetic focus reopens the drawer immediately, so the learner can never
   * dismiss it (LO-5081); CDS also sets `shouldCloseOnBlur: false` on XS, so
   * blurring cannot break the loop either.
   *
   * Swapping `popoverTrigger` via state cannot fix this: the drawer's close
   * path reads it before a React re-render could apply a new value.
   *
   * The return-focus call itself is scheduled via CDS's own `setTimeout(0)`
   * (FocusTrap's `onDeactivation`), not synchronous -- so restoring our
   * neutered `.focus` on a matching timer would race it with no guaranteed
   * winner. Restoring on the learner's next real `pointerdown`/`keydown`
   * instead has no such race: CDS's one programmatic call is swallowed
   * whenever it lands, and the learner's own next touch is what un-neuters
   * `.focus` again, before the browser's native focus-on-interaction runs.
   */
  const handlePopoverClose = () => {
    const element = inputRef.current;
    if (!isXs || !element) return;
    // `HTMLElement.prototype.focus` is an inherited accessor with no own
    // setter in some DOM implementations (jsdom included) -- a plain
    // `element.focus = fn` assignment throws there. Shadowing it with an own
    // property works everywhere, and restoring means removing that shadow
    // rather than writing the original back.
    Object.defineProperty(element, 'focus', { value: () => {}, configurable: true });
    const restore = () => {
      Reflect.deleteProperty(element, 'focus');
      document.removeEventListener('pointerdown', restore, true);
      document.removeEventListener('keydown', restore, true);
      restoreFocusRef.current = null;
    };
    restoreFocusRef.current = restore;
    document.addEventListener('pointerdown', restore, true);
    document.addEventListener('keydown', restore, true);
  };

  // Only the learner's own typing should be tracked as a "search" -- the
  // resync effect above calls `setInput` directly, bypassing this handler.
  const handleInputChange = (next: string) => {
    setInput(next);
    trackSearchInput(next);
  };

  return (
    <Autocomplete
      fullWidth
      ref={inputRef}
      {...(labelId ? { 'aria-labelledby': labelId } : { 'aria-label': _t('Current role') })}
      placeholder={_t('Add your current role')}
      value={input}
      onInputChange={handleInputChange}
      onSelectionChange={handleSelectionChange}
      inputProps={{ onKeyDown: handleKeyDown, onBlur: handleBlur }}
      popoverTrigger="focus"
      allowsCustomValue
      items={options}
      onClose={handlePopoverClose}
    >
      {(option) => (
        <Autocomplete.Option key={option.key} textValue={option.label}>
          {option.isCreate ? _t('Add “#{label}”', { label: option.label }) : option.label}
        </Autocomplete.Option>
      )}
    </Autocomplete>
  );
};

export default CurrentRoleEditor;
